import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Shield, Eye, EyeOff, Film, Lock, Users, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'

const LoginPage = () => {
  // Check if already authenticated
  const token = localStorage.getItem('token')
  if (token) {
    return <Navigate to="/success" replace />
  }

  // Form states
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    inviteCode: '',
    email: '',
    password: ''
  })

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in email and password')
      return
    }

    setLoading(true)
    
    try {
      const response = await fetch('/.netlify/functions/auth-login-simple-jwt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          inviteCode: formData.inviteCode || null
        })
      })

      const data = await response.json()

      if (data.success) {
        // Store tokens and user data in localStorage
        localStorage.setItem('token', data.token)
        localStorage.setItem('refreshToken', data.refreshToken)
        localStorage.setItem('user', JSON.stringify(data.user))
        
        toast.success('Login successful!')
        
        // Redirect to success page
        window.location.href = '/success'
      } else {
        toast.error(data.error || 'Login failed')
      }
    } catch (error) {
      console.error('Login error:', error)
      toast.error('Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-netflix-black flex">
      {/* Left side - Hero/Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-netflix-red to-red-900 p-12 flex-col justify-center">
        <div className="max-w-md">
          <div className="flex items-center space-x-3 mb-8">
            <Film className="w-12 h-12 text-white" />
            <h1 className="text-4xl font-bold text-white">
              VideoShare<span className="text-red-200">WEB</span>
            </h1>
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-6">
            Private Family Video Platform
          </h2>
          
          <div className="space-y-4 text-white">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-red-200" />
              <span>Enterprise-level security & protection</span>
            </div>
            <div className="flex items-center space-x-3">
              <Lock className="w-6 h-6 text-red-200" />
              <span>Invite-only access with authentication</span>
            </div>
            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-red-200" />
              <span>Private sharing with family & friends</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-red-200" />
              <span>Netflix-style streaming experience</span>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-black bg-opacity-20 rounded-lg">
            <p className="text-sm text-red-100">
              🔐 <strong>Maximum Privacy:</strong> Your videos are never publicly accessible. 
              All content requires valid authentication.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="card p-8">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-2 mb-4 lg:hidden">
                <Film className="w-8 h-8 text-netflix-red" />
                <h1 className="text-2xl font-bold text-white">VideoShareWEB</h1>
              </div>
              <h2 className="text-2xl font-bold text-white">Welcome Back</h2>
              <p className="text-gray-400 mt-2">Sign in to access your family videos</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Invitation Code (optional)
                </label>
                <input
                  type="text"
                  name="inviteCode"
                  value={formData.inviteCode}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Leave blank if existing user"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="input-field pr-10"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                New users require an invitation code to create an account.
                <br />
                Existing users can login without an invitation code.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage